"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";

/** Counts a value up once the card scrolls into view. */
function useCountUp(target: number, decimals = 0, durationMs = 1400) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setValue(target);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;
        started.current = true;
        observer.disconnect();

        const start = performance.now();
        const tick = (now: number) => {
          const progress = Math.min(1, (now - start) / durationMs);
          // Ease-out so the number decelerates into its final value.
          const eased = 1 - Math.pow(1 - progress, 3);
          setValue(target * eased);
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target, durationMs]);

  return {
    ref,
    display: value.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
  };
}

function Stat({
  target,
  decimals = 0,
  prefix = "",
  suffix = "",
}: {
  target: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
}) {
  const { ref, display } = useCountUp(target, decimals);

  return (
    <span ref={ref} className="text-[34px] font-black leading-none text-white lg:text-[40px]">
      {prefix}
      {display}
      <span className="text-cyan-400">{suffix}</span>
    </span>
  );
}

/** The published figures. Edit here — nothing else reads them. */
const STATS = {
  rating: 4.9,
  stability: 99,
  experience: 5,
  retention: 80,
  visits: 30000,
  downloads: 400000,
  clients: 40,
} as const;

const CARD =
  "flex flex-col gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-6 transition-colors duration-300 hover:border-sky-500/40";

export default function StatisticsSection() {
  const t = useTranslations("Home");
  const locale = useLocale();
  const isArabic = locale === "ar";

  /** Weekday initials under the 24/7 card. */
  const days = isArabic
    ? ["ح", "ن", "ث", "ر", "خ", "ج", "س"]
    : ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <section
      id="stats"
      dir={isArabic ? "rtl" : "ltr"}
      className="relative px-4 py-14 md:px-6 lg:px-8 lg:py-20"
    >
      <div className="mx-auto flex w-full max-w-290 flex-col gap-10">
        <header className="flex flex-col gap-4 items-start text-start">
          <h2 className="m-0 bg-[linear-gradient(89.18deg,#0CA5F0_15.53%,#FFFFFF_220.84%)] bg-clip-text text-[30px] font-black leading-[1.3] text-transparent lg:text-[40px]">
            {t("statsTitle")}
          </h2>
          <p className="m-0 max-w-170 text-[16px] leading-[1.9] text-white/65">
            {t("statsSubtitle")}
          </p>
          <Link
            href={`/${locale}/works`}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/25 px-6 text-[15px] font-bold text-white transition-colors duration-300 hover:bg-white hover:text-[#13192B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0CA5F0]"
          >
            {t("statsCta")}
          </Link>
        </header>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* 24/7 availability */}
          <article className={CARD}>
            <span className="text-[34px] font-black leading-none text-white lg:text-[40px]">
              24<span className="text-cyan-400">/7</span>
            </span>
            <p className="m-0 text-[14px] leading-[1.7] text-white/60">{t("statsPanelSupportLabel")}</p>
            <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
              {days.map((day, index) => (
                <span
                  key={`${day}-${index}`}
                  className="flex size-7 items-center justify-center rounded-full bg-[#1b2436] text-[12px] text-white/70"
                >
                  {day}
                </span>
              ))}
            </div>
          </article>

          {/* Average rating */}
          <article className={CARD}>
            <Stat target={STATS.rating} decimals={1} suffix="/5" />
            <p className="m-0 text-[14px] leading-[1.7] text-white/60">{t("statsPanelRatingLabel")}</p>
            <div className="mt-auto flex gap-1.5 pt-3" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, index) => (
                <span
                  key={index}
                  className={`h-1.5 flex-1 rounded-full ${index < 4 ? "bg-[linear-gradient(90deg,#0CA5F0,#5A6BE0)]" : "bg-[linear-gradient(90deg,#0CA5F0,#5A6BE0)] opacity-60"}`}
                />
              ))}
            </div>
          </article>

          {/* Stability — the hero tile, spans two rows on wide screens */}
          <article className={`${CARD} items-center justify-center text-center lg:row-span-2`}>
            <div className="relative grid size-44 place-items-center lg:size-52">
              <svg viewBox="0 0 120 120" className="absolute inset-0 size-full -rotate-90" aria-hidden="true">
                <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                <circle
                  cx="60" cy="60" r="52" fill="none" stroke="url(#statRing)" strokeWidth="6"
                  strokeLinecap="round" strokeDasharray="327" strokeDashoffset="3"
                  className="stat-ring"
                />
                <defs>
                  <linearGradient id="statRing" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#0CA5F0" />
                    <stop offset="1" stopColor="#5A6BE0" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="text-[40px] font-black leading-none text-white lg:text-[46px]">
                <Stat target={STATS.stability} suffix="%" />
              </span>
            </div>
            <p className="m-0 text-[14px] leading-[1.7] text-white/60">{t("statsBottomStabilityLabel")}</p>
          </article>

          {/* Years of experience */}
          <article className={CARD}>
            <Stat target={STATS.experience} suffix="+" />
            <p className="m-0 text-[14px] leading-[1.7] text-white/60">{t("statsCenterExperienceLabel")}</p>
            <div className="mt-auto flex items-end gap-1.5 pt-3" aria-hidden="true">
              {[40, 60, 80, 100].map((height) => (
                <span
                  key={height}
                  style={{ height: `${height / 5}px` }}
                  className="flex-1 rounded-full bg-[linear-gradient(90deg,#0CA5F0,#5A6BE0)]"
                />
              ))}
            </div>
          </article>

          {/* Returning clients */}
          <article className={CARD}>
            <Stat target={STATS.retention} suffix="%" />
            <p className="m-0 text-[14px] leading-[1.7] text-white/60">{t("statsTopRetentionLabel")}</p>
            <div className="mt-auto flex flex-wrap gap-1.5 pt-3" aria-hidden="true">
              {Array.from({ length: 10 }).map((_, index) => (
                <span
                  key={index}
                  className={`size-2 rounded-full ${index < 8 ? "bg-[#0CA5F0]" : "bg-white/15"}`}
                />
              ))}
            </div>
          </article>

          {/* Visits */}
          <article className={CARD}>
            <Stat target={STATS.visits} suffix="+" />
            <p className="m-0 text-[14px] leading-[1.7] text-white/60">{t("statsTopVisitsLabel")}</p>
            <span className="mt-auto flex size-9 items-center justify-center rounded-full bg-[#1b2436] text-cyan-400" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="size-5" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
                <path d="M3 12h18M12 3c2.5 2.7 2.5 15 0 18M12 3c-2.5 2.7-2.5 15 0 18" stroke="currentColor" strokeWidth="1.7" />
              </svg>
            </span>
          </article>

          {/* Downloads */}
          <article className={CARD}>
            <Stat target={STATS.downloads} suffix="+" />
            <p className="m-0 text-[14px] leading-[1.7] text-white/60">{t("statsPanelDownloadsLabel")}</p>
            <span className="mt-auto flex size-9 items-center justify-center rounded-full bg-[#1b2436] text-cyan-400" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="size-5" fill="none">
                <path d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </article>

          {/* Happy clients */}
          <article className={`${CARD} sm:col-span-2 lg:col-span-1`}>
            <Stat target={STATS.clients} suffix="+" />
            <p className="m-0 text-[14px] leading-[1.7] text-white/60">{t("statsBottomClientsLabel")}</p>
            <div className="mt-auto grid grid-cols-10 gap-1.5 pt-3" aria-hidden="true">
              {Array.from({ length: 50 }).map((_, index) => (
                <span key={index} className={`size-1.5 rounded-full ${index < STATS.clients ? "bg-[#5A6BE0]" : "bg-white/12"}`} />
              ))}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
